import _ from "lodash";
import queryString from 'qs'
import { logger } from '@/utils/log';
import { getPlanTypeByCategory } from '../parse';
import { AUDIO_RATE_TYPES, AUDIO_SUB_TYPES, planTypes, PBX_CATEGORYS } from "@/common/constants";
import { flowTypes } from '@/common/service/page-config';
import { isFromCancelCampaign } from '@/views/config/coupon/helper/utils';

export function addToCart(plan, defaultLicense = 0) {
  if (!plan) return;
  if (plan.isPPA) {
    plan.cartInfo.selected_number = 1;
  } else if (plan.min && defaultLicense && plan.min > defaultLicense) {
    plan.cartInfo.selected_number = plan.min;
  } else {
    plan.cartInfo.selected_number = defaultLicense || plan.min || 1;
  }
  return plan;
}

export function addToCartByCategoryAndBillingCycle(planMapFlat = [], planType, category, billingCycle, defaultLicense, calculateLicenseFn) {
  if (!planType) return;

  const typePlans = _.filter(planMapFlat, item => item.type === planType);
  const categoryReg = new RegExp(`^${category}$`, 'i');
  const categoryPlans = _.filter(planMapFlat,item => category && categoryReg.test(item.category));
  const plan = _.find(categoryPlans.length > 0 ? categoryPlans: typePlans, item => item.billing_cycle === billingCycle); // FREE TRIAL - it's possible input `category` without capacity but typePlans has. so filter again with typePlans and billing cycle as second choice
  if(!typePlans.length) return null;
  if(!plan) {
    const preloadPlan = categoryPlans[0] || typePlans[0];
    logger.info(`Can't find ${category} and ${billingCycle}`);
    if (calculateLicenseFn && _.isFunction(calculateLicenseFn)) defaultLicense = calculateLicenseFn?.(preloadPlan);
    return addToCart(preloadPlan, defaultLicense);
  } else {
    if (calculateLicenseFn && _.isFunction(calculateLicenseFn)) defaultLicense = calculateLicenseFn?.(plan);
    return addToCart(plan, defaultLicense);
  }
}


export function addToCartByType(planMapFlat = [], planType, defaultLicense) {
  if (!planType) return;
  const typePlans = _.filter(planMapFlat, item => item.type === planType);
  if(!typePlans.length) return null;
  return addToCart(typePlans[0], defaultLicense);
}

export function addToCartByPlanCode(planMapFlat, planCode, defaultLicense) {
  const plan = planMapFlat.find(item => item.plan_code === planCode);
  if(!plan) {
    logger.info(`Can't find ${planCode} in ${planMapFlat}`);
  } else {
    return addToCart(plan, defaultLicense);
  }
}

export function addToCartByPlanType(planMapFlat, planType, defaultLicense) {
  const plan = planMapFlat.find(item => item.plan_type === planType);
  if(!plan) {
    logger.info(`Can't find ${planType} in ${planMapFlat}`);
  } else {
    return addToCart(plan, defaultLicense);
  }
}

export function planQueryParser(planStr) {
  const defaultResult = {
    fromType: undefined,
    category_codes: [],
  };

  // transfer array to string with from/from_sub/plan params
  if (planStr && Array.isArray(planStr)) {
    planStr = _.get(planStr, [0]);
  }

  const _pbxFromParse = p => {
    const parseReg = /^(pbx|zonebundle|zoneproplus|zopschcmp)(_(.+))?$/i;
    let res = parseReg.exec(p);
    if(!res) return null;

    const [, category, , category_codes] = res;
    let codes = (category_codes || '').split(',').filter(Boolean);

    if(!codes.length) return null;

    if (!window?.newBillingConfig?.enableBizPlusJapanOnlineBuy) {
      codes = codes.map(code => {
        // replace biz plus japan code to biz plus us plan code if online can not buy biz plus japan rate plan
        if (code === 'zonebizpzju' || code?.includes?.('zonebizpzju')) {
          return 'zonebizpzvu';
        }
        return code;
      });
    }

    if (window?.newBillingConfig?.hideZoomPhoneUnlimitedJP) {
      codes = codes.map(code => {
        if (code?.includes?.('ZJU')) {
          return 'ZJM';
        }
        return code;
      });
    }

    return {
      fromType: getPlanTypeByCategory(category),
      category_codes: [...codes, PBX_CATEGORYS.ZVP],
    };
  }

  const _zClipsParse = p => {
    if (p === "zclips") {
      const category_codes = ["zoom_clips"];
      return {
        fromType: getPlanTypeByCategory(category_codes[0]),
        category_codes: category_codes,
      };
    }
    return null;
  }

  const _commonFromParse = p => {
    if(!p) return null;

    const category_codes = p.split(',').filter(Boolean);
    return {
      fromType: getPlanTypeByCategory(category_codes[0]),
      category_codes: category_codes,
    };
  }

  const _zeConsumptionParse = p => {
    const parseReg = /^(zecc_yearly|zeca_yearly)(\d+)?$/i;
    if(parseReg.test(p)) {
      const [category] = parseReg.exec(p) || [];

      return {
        fromType: getPlanTypeByCategory(category),
        category_codes: [category],
      };
    }
    return null;
  }

  if(!planStr) return defaultResult;

  let res = _pbxFromParse(planStr);
  if(!res) {
    res = _zeConsumptionParse(planStr);
  }
  if(!res) {
    res = _zClipsParse(planStr);
  }
  if(!res) {
    res = _commonFromParse(planStr);
  }
  return res || defaultResult;
}

export const de$type = (type) => type.split('$')?.[0];

export function getAudioCountriesParams(store) {
  const getters = store.getters;
  const audioPlan = getters.getSelectedPlans.find(item => item.type === planTypes.TOLLFREE && item.category === AUDIO_SUB_TYPES.TOLL_FREE);
  const unlimitedAudioPlan = getters.getSelectedPlans
  .find(item => item.type === planTypes.TOLLFREE && item.category === AUDIO_SUB_TYPES.UNLIMITED);
  if(audioPlan) {
    const { tollFreeProfileData } = audioPlan.cartInfo;
    return tollFreeProfileData
  } else if(unlimitedAudioPlan) {
    return {
      [AUDIO_RATE_TYPES.TOLL_FREE]: "US,CA",
      [AUDIO_RATE_TYPES.CALL_OUT]: "US,CA",
      [AUDIO_RATE_TYPES.PREMIUM]: ""
    }
  }
  return null;
}

export const getCurrentPlanCycleDepFirstPlanForInit = (planList = [], boughtPlans = [], planType) => {
  // get current depend base plan list
  const dependCyclePlanList = _.chain(planList)
    .filter((p) => p.type === planType)
    .get([0, 'billingCycleDependBasePlanTypeList'], [])
    .value();

  // get max billing cycle value with depend base plan list
  return _.chain(boughtPlans)
    .filter((p) => _.includes(dependCyclePlanList, p.type))
    .orderBy((p) => p.cycleValue, 'desc')
    .get(0)
    .value();
}

export const MIX_BUY_FLOW_UTILS = {
  planIsEdit(flowPlanTypeMap, planType) {
    return _.includes(flowPlanTypeMap?.[flowTypes.edit], planType);
  },
  planIsAdd2Cart(flowPlanTypeMap, planType) {
    return _.includes(flowPlanTypeMap?.[flowTypes.add2cart], planType);
  },
}
/**
 *
 * @param {Object} filter
 * @param {Array} filter.plans - all plans list
 * @param {String} filter.type - target plan type
 * @param {String} filter.category - target plan category
 * @param {String} filter.billingCycle - target plan billing cycle
 */
export default function findDefaultPlan(filter) {
  const { plans = [], type, category, billingCycle } = filter
  let typePlans = plans
  if (type) {
    typePlans = plans.filter(item => item.type === type);
  }
  if (!typePlans.length) return null
  let categoryPlans = typePlans.filter(item => item.category === category);
  if (!categoryPlans.length) {
    categoryPlans = typePlans;
  }
  return categoryPlans.find(item => item.billing_cycle === billingCycle) || categoryPlans[0]
}

export const isFromCancelCampaignWithPendingCase = (boughtPlans = [], currentPlanCode = "") => {
  return isFromCancelCampaign() && _.some(boughtPlans, (p) => {
    return p?.plan_code === currentPlanCode && p?.cartInfo?.has_same_pending_plan;
  });
}

export function getTargetUserIdParam() {
  const { targetUserId } = queryString.parse(location.search.slice(1)) || {};
  if (targetUserId) {
    return { targetUserId }
  }
  return {}
}
